import classnames from 'classnames';
import type * as React from 'react';

import { REQUIRED_STATE_TEXT } from '../RequiredStateText';

import './Radio.css';

export interface RadioProps<T extends string | boolean = string> {
  /**
   * Name attribute for the radio group (all radios in a group should have the same name)
   */
  name: string;
  /**
   * Label for the radio button. Always used as the input's accessible name,
   * even when labelContent is provided - keep this as plain, readable text.
   */
  label: string;
  /**
   * Optional rich content to render in place of the plain-text label (e.g. a
   * styled preview). Purely visual - label is still what screen readers
   * announce, so labelContent must not be the only way to convey meaning.
   */
  labelContent?: React.ReactNode;
  /**
   * Value of this radio option
   */
  value: T;
  /**
   * Whether this radio button is checked
   */
  checked?: boolean;
  /**
   * Whether the radio button is disabled
   */
  disabled?: boolean;
  /**
   * Callback when radio value changes
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Additional class name
   */
  className?: string;
  /**
   * Whether the radio belongs to a required group. Set on the input rather than the
   * fieldset: `aria-required` on a group is not exposed in the accessibility tree at
   * all, so on the fieldset it reached nothing.
   */
  required?: boolean;
  /**
   * Whether the group is reporting an error. Same reasoning as `required`: it has to be
   * on the input, which is also the element focus can land on.
   */
  invalid?: boolean;
  /** Id of the element holding the group's error message. */
  describedBy?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function Radio<T extends string | boolean = string>({
  name,
  label,
  labelContent,
  value,
  checked = false,
  disabled = false,
  onChange,
  className,
  required,
  invalid,
  describedBy,
  'data-testid': dataTestId,
}: RadioProps<T>) {
  // ACP-2092: JAWS announces only the radio's own name when tabbing into a group — not
  // the group name, and not aria-required. Measured on the Add User modal: it said just
  // "No". So the state has to ride on each option's name. See RequiredStateText.
  const accessibleName = required ? `${label} ${REQUIRED_STATE_TEXT}` : label;

  const radioClasses = classnames('access-radio', className, {
    'access-radio--checked': checked,
    'access-radio--disabled': disabled,
  });
  const baseTestId = dataTestId ?? 'radio';

  const stringValue = String(value);
  const id = `${name}-${stringValue}`;

  return (
    <label
      className={radioClasses}
      data-testid={baseTestId}
      htmlFor={id}>
      <input
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        aria-label={accessibleName}
        aria-required={required || undefined}
        checked={checked}
        className='access-radio__input'
        data-testid={`${baseTestId}-input`}
        disabled={disabled}
        id={id}
        name={name}
        readOnly={!onChange}
        type='radio'
        value={stringValue}
        onChange={onChange}
      />
      <span
        className='access-radio__label'
        data-testid={`${baseTestId}-label`}>
        {labelContent ?? label}
      </span>
    </label>
  );
}

export default Radio;
